import { Text, TouchableOpacity, View } from "react-native";
import { Button } from "ui/gluestack/button";
import {
  UploadCloudIcon,
  UploadCloudTwoIcon,
} from "ui/gluestack/icon/UploadCloud";
import { AlertCircleIcon, Icon } from "ui/gluestack/icon";
import * as DocumentPicker from "expo-document-picker";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import {
  Controller,
  ControllerProps,
  FieldError,
  FieldValues,
} from "react-hook-form";
import { generalAllowedExtensions } from "app/constants/allowedExtensionsConstants";
import { ThrelsFileType } from "ui/types/ThrelsFileType";
import { ViewDocument } from "app/components/atoms/ViewDocument";

import {
  SignaturePadModal,
  SignaturePadModalRef,
} from "./modals/SignaturePadModal";
import { useRef } from "react";
import { requiredValidation } from "ui/utils/Validators";
import { base64ToDocumentPickerAsset } from "ui/utils/signatureUtils";

interface SignaturePadProps<TFieldValues extends FieldValues = FieldValues> {
  id?: string;
  control: ControllerProps<TFieldValues>["control"];
  name: ControllerProps<TFieldValues>["name"];
  isRequired?: boolean;
  rules?: ControllerProps<TFieldValues>["rules"];
  label?: string;
  isReadOnly?: boolean;
}
export function FormControlledSignaturePad({
  id,
  control,
  name,
  isRequired,
  rules,
  label,
  isReadOnly,
}: SignaturePadProps) {
  const signaturePadModalRef = useRef<SignaturePadModalRef>(null);
  const localRules = {
    required: isRequired ? requiredValidation(label ?? "Field") : undefined,
    ...rules,
  };

  return (
    <Controller
      key={id}
      control={control}
      name={name}
      rules={localRules}
      render={({ fieldState: { error }, field: { value, onChange } }) => {
        return (
          <View>
            {!value?.length ? (
              <TouchableOpacity
                onPress={() => signaturePadModalRef.current?.show()}
              >
                <View className="w-[286px] h-[260px] mb-0  bg-background-50 border border-border-300 border-dashed rounded-xl justify-center items-center hover:bg-background-100">
                  <Icon
                    as={UploadCloudTwoIcon}
                    className="w-11 h-11 text-gray-200"
                  />
                  <Text className="text-center text-gray-500">Sign</Text>
                </View>
              </TouchableOpacity>
            ) : (
              <View className="flex flex-row flex-wrap gap-4">
                {value.map((file, index) => (
                  <View key={index}>
                    <ViewDocument document={file} />
                    <TouchableOpacity
                      onPress={!isReadOnly ? () => onChange(null) : undefined}
                    >
                      <Text className="text-primary-500 my-2.5 font-medium">
                        Remove
                      </Text>
                    </TouchableOpacity>
                  </View>
                ))}
              </View>
            )}
            {error ? (
              <View className="flex-row gap-x-1 items-center mt-3">
                <Icon
                  as={AlertCircleIcon}
                  className="text-error-500"
                  size="sm"
                />
                <Text className="text-error-500 text-sm">{error?.message}</Text>
              </View>
            ) : null}

            <SignaturePadModal
              ref={signaturePadModalRef}
              onUse={(signature) => {
                if (signature) {
                  // Convert base64 PNG to DocumentPickerAsset (ThrelsFileType)
                  const signatureFile = base64ToDocumentPickerAsset(
                    signature,
                    `signature-${label?.toLowerCase().replace(/\s+/g, "-") || "document"}.png`,
                  );
                  // Store as array to match the expected format
                  onChange([signatureFile]);
                }
              }}
              title={label}
            />
          </View>
        );
      }}
    ></Controller>
  );
}
